import React, { useState } from 'react';
import { Box, Card, Flex, Text } from 'rebass/styled-components';
import { InfoBox } from 'components/atoms/InfoBox';
import { Button, Dropdown } from '@getro/rombo';
import { FileSpreadsheet, Plus, User, UserPlus } from 'lucide-react';
import { Link, NavLink } from 'react-router-dom';

import UpgradeModal from 'components/organisms/upgradeModal';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useTierGating } from 'hooks/useTierGating';
import usePageHook from '../../hooks/usePageHook';
import { useNetworkOverview } from './hooks/useNetworkOverview';
import { Playbooks } from './playbooks';
import { useGreetings } from './hooks/useGreetings';
import { NetworkBanner } from './networkBanner';

export const ConnectHome = () => {
  const {
    networkAdminsCount,
    alliesCount,
    isLoading,
    contactsCount,
    isLoadingContacts,
    activeNetwork,
    user,
    desktopWidth,
    requestIntrosCount,
    sharedListsCount,
  } = useNetworkOverview();
  const greetings = useGreetings();
  const [showConnectBanner, setShowConnectBanner] = useLocalStorage('show-connect-banner', true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { hasCapability, isContactsLimitReached } = useTierGating();
  const canAddContacts = hasCapability('contacts_import');
  const upgradeFeatureName = !canAddContacts ? 'contacts_import' : 'contacts_limit';

  usePageHook('Connect home');

  const renderAddContacts = () => {
    if (!canAddContacts || isContactsLimitReached) {
      return (
        <>
          <Button variant="tertiary" size="small" onClick={() => setShowUpgradeModal(true)} sx={{ height: '32px' }}>
            <Flex alignItems="center" fontSize="14px">
              <Box as={UserPlus} mr="2" width="16px" height="16px" aria-hidden="true" />
              Add contacts
            </Flex>
          </Button>
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            featureName={upgradeFeatureName}
          />
        </>
      );
    }

    return (
      <Dropdown
        triggerProps={{
          variant: 'tertiary',
          size: 'small',
          id: 'add-contacts-dropdown-trigger',
          children: (
            <Flex alignItems="center" fontSize="14px">
              <Box as={UserPlus} mr="2" width="16px" height="16px" aria-hidden="true" />
              Add contacts
            </Flex>
          ),
          sx: {
            height: '32px',
          },
        }}
      >
        <Card px="0" py="8px" width="208px">
          <Flex
            as={NavLink}
            to={`/networks/${activeNetwork.slug}/contact/add`}
            height="32px"
            px="16px"
            sx={{
              fontSize: '14px',
              gap: '8px',
              alignItems: 'center',
              textDecoration: 'none',
              ':hover': { bg: 'neutral.50' },
            }}
          >
            <Box as={User} width="16px" height="16px" aria-hidden="true" />
            Add single contact
          </Flex>
          <Flex
            as={NavLink}
            to={`/networks/${activeNetwork.slug}/import-contacts/csv`}
            height="32px"
            px="16px"
            sx={{
              fontSize: '14px',
              gap: '8px',
              alignItems: 'center',
              textDecoration: 'none',
              ':hover': { bg: 'neutral.50' },
            }}
          >
            <Box as={FileSpreadsheet} width="16px" height="16px" aria-hidden="true" />
            Import CSV
          </Flex>
        </Card>
      </Dropdown>
    );
  };

  return (
    <Flex flexDirection="column" mt="40px" mx="auto" px="16px" width="100%" maxWidth="850px" sx={{ gap: '64px' }}>
      <Flex sx={{ gap: '8px', flexDirection: 'column' }}>
        <Text fontSize="22px" fontWeight="700" lineHeight="120%" color="text.dark">
          👋 Hey {user?.firstName}
        </Text>
        <Text color="text.main" fontSize="16px" fontWeight="400" lineHeight="120%">
          {greetings}
        </Text>
      </Flex>
      {showConnectBanner && <NetworkBanner networkSlug={activeNetwork.slug} setShowBanner={setShowConnectBanner} />}
      <Flex sx={{ flexDirection: 'column', gap: '16px' }}>
        <Flex sx={{ gap: '8px', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Text color="text.dark" fontSize="16px" fontWeight="600" lineHeight="1.2">
            Network overview
          </Text>
          <Flex sx={{ gap: '8px', alignItems: 'center' }}>
            {renderAddContacts()}
            <Button
              size="small"
              as={Link}
              to={`/networks/${activeNetwork.slug}/settings/team-members`}
              icon={Plus}
              iconGap="8px"
              variant="tertiary"
            >
              Invite team
            </Button>
          </Flex>
        </Flex>
        <Flex alignItems="strech" sx={{ gap: '16px', flexDirection: ['column', 'row'], mb: '16px' }}>
          <InfoBox
            width={['100%', desktopWidth]}
            title="Intro requests"
            count={requestIntrosCount}
            loading={isLoadingContacts}
            sx={{ height: 'auto' }}
            infoText={
              <Flex sx={{ flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                <Text>Total number of intro requests your team has received and sent.</Text>
              </Flex>
            }
          />
          <InfoBox
            title="Shared lists"
            width={['100%', desktopWidth]}
            count={sharedListsCount}
            loading={isLoading}
            infoText={
              <Flex sx={{ flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                <Text>Total number of lists your team is sharing.</Text>
                <Text>Sharing lists lets others request intros, unlocking value from your network.</Text>
              </Flex>
            }
            sx={{ height: 'auto' }}
          />
        </Flex>
        <Flex alignItems="strech" sx={{ gap: '16px', flexDirection: ['column', 'row'], mb: '16px' }}>
          <InfoBox
            width={['100%', desktopWidth]}
            title="Contacts"
            count={contactsCount}
            loading={isLoadingContacts}
            sx={{ height: 'auto' }}
            infoText={
              <Flex sx={{ flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                <Text>
                  Total number of contacts in your network – from you, your team, allies, and your talent network.
                </Text>
                <Text>The more contacts you have, the more warm intros you can uncover!</Text>
              </Flex>
            }
          />
          <InfoBox
            width={['100%', desktopWidth]}
            title="Network admins"
            count={networkAdminsCount}
            loading={isLoading}
            sx={{ height: 'auto' }}
            infoText={
              <Flex sx={{ flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                <Text>Team members who can manage your Getro network. </Text>
                <Text>Admins can share their contacts, and help unlock value from your extended network. </Text>
              </Flex>
            }
          />
          <InfoBox
            title="Allies sharing their contacts"
            width={['100%', desktopWidth]}
            count={alliesCount}
            loading={isLoading}
            infoText={
              <Flex sx={{ flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                <Text>
                  External collaborators (like investors, advisors, or founders) who are sharing their contacts with
                  your network.
                </Text>
                <Text>Invite trusted allies to uncover more warm intros!</Text>
              </Flex>
            }
            sx={{ height: 'auto' }}
          />
        </Flex>
      </Flex>
      {activeNetwork?.kind === 'ecosystem' && <Playbooks goals={activeNetwork?.userGoals} />}
    </Flex>
  );
};
